/**
 * Simple Coffee design tokens — the single source of truth for brand values.
 *
 * Rule 16/17: never write a brand colour literal inside a component. Import
 * from here, or use the matching CSS custom property from `styles/tokens.css`.
 * The two files are kept in sync by `tokens.test.ts`.
 */

export const colors = {
  /** Simple Turquoise — primary brand colour, primary actions, active states. */
  primary: '#00D7DE',
  /** Turquoise Deep — hover / pressed / active fills, and large text only. */
  primaryDeep: '#008D93',
  /**
   * Turquoise Ink — derived from Turquoise Deep for TEXT and icons.
   *
   * Turquoise Deep reaches only 4.01:1 on white, below the 4.5:1 WCAG AA
   * threshold for body text, so it stays a fill colour. This darker step keeps
   * the same hue at 5.34:1 on white and 5.34:1 under white text (DA-05).
   */
  primaryInk: '#00777C',
  /** Turquoise Soft — secondary backgrounds, selected states, panels. */
  primarySoft: '#EAFBFC',
  /** Simple Yellow — accent, badges, highlights, secondary CTAs. */
  accent: '#FECE00',
  /** Simple Charcoal — text, headings, icons, borders, navigation. */
  charcoal: '#3D3E52',
  /** White — surfaces, cards, forms, reading areas. */
  white: '#FFFFFF',
  /** Warm Cream — editorial storytelling backgrounds. */
  cream: '#F7F3EA',
} as const;

/**
 * Feedback colours, each as a set of three. Never signal state with colour
 * alone (accessibility): pair these with an icon and explicit text.
 *
 * The base was the only step until Phase 28, which found the consequence: every
 * component that needed a tint or a darker text step mixed its own hex — twelve
 * of them across `Alert`, `Badge`, `Button` and `StatTile`, with the same
 * success tint written out three separate times. Rule 16 says components carry
 * no brand literals, and the system had left them nowhere else to go.
 *
 * - `base` — the border, the fill, the icon. Not body text on white.
 * - `soft` — the ground a message sits on.
 * - `ink`  — text on `soft`. Checked against it in `contrast.test.ts`.
 */
export const feedback = {
  success: '#1B8A5A',
  successSoft: '#E6F4ED',
  successInk: '#14603F',
  warning: '#B26A00',
  warningSoft: '#FDF1DD',
  warningInk: '#7D4B00',
  danger: '#C2364B',
  dangerSoft: '#FBE9EC',
  dangerInk: '#8D2536',
  /** The pressed step of a destructive button. */
  dangerDeep: '#A32A3C',
  info: '#008D93',
} as const;

/** Neutral ramp derived from charcoal, for borders and muted text. */
export const neutrals = {
  charcoal900: '#2B2C3B',
  charcoal700: '#3D3E52',
  charcoal500: '#6B6C80',
  charcoal300: '#A9AAB8',
  charcoal100: '#E4E5EB',
  charcoal050: '#F4F5F7',
} as const;

/** 4px base scale. */
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
} as const;

/** Rounded but never bubbly (DA-08). */
export const radii = {
  sm: '6px',
  md: '10px',
  lg: '16px',
  pill: '999px',
} as const;

/** Flat design: shadows stay subtle and are used sparingly. */
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(61, 62, 82, 0.08)',
  md: '0 4px 12px rgba(61, 62, 82, 0.10)',
} as const;

export const typography = {
  fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
  sizes: {
    h1: '40px',
    h2: '30px',
    h3: '22px',
    body: '16px',
    label: '14px',
    caption: '13px',
  },
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.7,
  },
} as const;

export const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1440px',
} as const;

/** Short, fast micro-interactions only (section 13). */
export const motion = {
  fast: '120ms',
  normal: '180ms',
  slow: '240ms',
  easing: 'cubic-bezier(0.2, 0, 0.2, 1)',
} as const;

/**
 * Categorical palette for charts — eight slots in a FIXED order.
 *
 * The order is the colour-blindness safety mechanism, not decoration: slots are
 * assigned in sequence and never cycled or reordered per chart. Turquoise leads
 * (the brand), the accent amber follows as its darkened chart step, and the
 * remaining hues were snapped to steps that clear every check.
 *
 * Validated with the palette validator: every slot inside the OKLCH lightness
 * band, chroma above the floor, ≥3:1 against a white surface, and a worst
 * adjacent pair of ΔE 17.2 under deuteranopia (target ≥8). Re-run the validator
 * before changing any value here — see docs/design-system.md.
 *
 * Simple Yellow (#FECE00) is deliberately absent: at OKLCH L 0.87 it sits above
 * the band and reaches only 1.5:1 on white, so it stays a UI accent and appears
 * in charts as slot 2's darker step.
 */
export const chartPalette = [
  '#1D9999', // turquoise — the brand, and the single-series default
  '#815C0A', // amber
  '#6D41A9', // violet
  '#286F2F', // green
  '#0057C0', // blue
  '#963E35', // brick
  '#D058AE', // magenta
  '#5C6413', // olive
] as const;

/** Chart furniture. Marks carry the series colour; text never does. */
export const chartInk = {
  /** Single-series marks: histograms, distributions, one-measure bars. */
  series: chartPalette[0],
  /** Hairline gridlines and axes — one step off the surface, recessive. */
  grid: neutrals.charcoal100,
  axis: neutrals.charcoal300,
  /** Axis and value text. */
  label: neutrals.charcoal500,
  /** The gap and ring that separate touching marks (2px of the surface). */
  surface: colors.white,
  /** Missing data, drawn as absence rather than as another category. */
  muted: neutrals.charcoal300,
} as const;

export const tokens = {
  colors,
  feedback,
  neutrals,
  spacing,
  radii,
  shadows,
  typography,
  breakpoints,
  motion,
  chartPalette,
  chartInk,
} as const;

export type Tokens = typeof tokens;
